import React, { useEffect, useState } from 'react';
import { Box, IconButton, List, ListItem, ListItemButton } from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { Icon } from 'src/mtska-frontend-app-component/assets';

const ComponentSlider = ({ report, maxWidth = '100%', height = 'auto', showTopPagination = false }) => {
    const sections = Array.isArray(report) ? report : [];
    const [activeSection, setActiveSection] = useState(0);
    const [activeStep, setActiveStep] = useState(0);
    const [maxSteps, setMaxSteps] = useState(0);

    const [activeSectionStep, setActiveSectionStep] = useState(0);
    const [maxSectionStep, setMaxSectionStep] = useState(0);

    /** Accordion: solo un primo livello aperto; `null` = tutti chiusi. */
    const [expandedSectionIndex, setExpandedSectionIndex] = useState(null);

    useEffect(() => {
        setMaxSteps(
            sections.reduce((total, section) => total + (Array.isArray(section?.pages) ? section.pages.length : 0), 0),
        );
    }, [sections]);

    useEffect(() => {
        if (sections.length === 0) {
            setActiveSection(0);
            setActiveSectionStep(0);
            setExpandedSectionIndex(null);
            return;
        }
        if (activeSection >= sections.length) {
            setActiveSection(0);
            setActiveSectionStep(0);
            setExpandedSectionIndex(0);
        }
    }, [sections, activeSection]);

    useEffect(() => {
        const pages = sections[activeSection]?.pages || [];
        setMaxSectionStep(pages.length);
        if (activeSectionStep >= pages.length) {
            setActiveSectionStep(0);
        }
    }, [sections, activeSection, activeSectionStep]);

    useEffect(() => {
        let previousPages = 0;
        for (let i = 0; i < activeSection; i++) {
            previousPages += sections[i]?.pages?.length || 0;
        }
        setActiveStep(previousPages + activeSectionStep);
    }, [sections, activeSection, activeSectionStep]);

    const handleNext = () => {
        const currentSection = sections[activeSection];
        if (!currentSection || !Array.isArray(currentSection.pages) || currentSection.pages.length === 0) return;
        const nextPageIndex = activeSectionStep + 1;

        if (nextPageIndex < currentSection.pages.length) {
            setActiveSectionStep(nextPageIndex);
            setExpandedSectionIndex(activeSection);
        } else {
            const nextSectionIndex = activeSection + 1;
            if (nextSectionIndex < sections.length) {
                setActiveSection(nextSectionIndex);
                setActiveSectionStep(0);
                setExpandedSectionIndex(nextSectionIndex);
            } else {
                setActiveSection(0);
                setActiveSectionStep(0);
                setExpandedSectionIndex(0);
            }
        }
    };

    const handleBack = () => {
        if (sections.length === 0) return;
        if (activeSectionStep > 0) {
            setActiveSectionStep(activeSectionStep - 1);
            setExpandedSectionIndex(activeSection);
        } else {
            const prevSectionIndex = activeSection - 1;
            if (prevSectionIndex >= 0) {
                const lastPage = (sections[prevSectionIndex]?.pages?.length || 1) - 1;
                setActiveSection(prevSectionIndex);
                setActiveSectionStep(lastPage);
                setExpandedSectionIndex(prevSectionIndex);
            } else {
                const lastSectionIndex = sections.length - 1;
                const lastPage = (sections[lastSectionIndex]?.pages?.length || 1) - 1;
                setActiveSection(lastSectionIndex);
                setActiveSectionStep(lastPage);
                setExpandedSectionIndex(lastSectionIndex);
            }
        }
    };

    const goTo = (section, page) => {
        setActiveSection(section);
        setActiveSectionStep(page);
        setExpandedSectionIndex(section);
    };

    const activePage = sections[activeSection]?.pages?.[activeSectionStep];
    const canNavigate = maxSectionStep > 0 && !!activePage;

    return (
        <Box className={'mtska-slider'} sx={{ maxWidth: maxWidth, position: 'relative' }}>
            <List className="mtska-slider-page-index">
                {sections.map((section, index) => (
                    <React.Fragment key={index}>
                        <ListItem disablePadding>
                            <ListItemButton
                                onClick={() => goTo(index, 0)}
                                selected={expandedSectionIndex === index}
                                className="mtska-slider-section-header"
                            >
                                <Icon icon={section.icon} className="icon" />
                                {section.title}
                            </ListItemButton>
                        </ListItem>
                        {expandedSectionIndex === index && (
                            <List disablePadding>
                                {section.pages.map((page, pageIndex) => (
                                    <ListItem
                                        key={pageIndex}
                                        sx={{ pl: 4, cursor: 'pointer' }}
                                        onClick={() => goTo(index, pageIndex)}
                                        className={'active' + (pageIndex === activeSectionStep && index === activeSection ? ' current' : '')}
                                    >
                                        <Icon icon={page.icon} className="icon" />
                                        {page.title}
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </React.Fragment>
                ))}
            </List>

            <Box
                sx={{
                    width: '100%',
                    ...(height !== 'auto' ? { height } : {}),
                }}
                className="mtska-slider-page-wrapper"
            >
                {activePage?.component || null}
            </Box>

            <Box className="mtska-slider-pager">
                <IconButton
                    size="small"
                    onClick={handleBack}
                    disabled={!canNavigate}
                    className='slider-arrow slider-arrow-left'
                >
                    <KeyboardArrowLeft />
                </IconButton>
                <Box className="mtska-page-indicator">
                    {activeStep + 1} / {maxSteps}
                </Box>
                <IconButton
                    size="small"
                    onClick={handleNext}
                    disabled={!canNavigate}
                    className='slider-arrow slider-arrow-right'
                >
                    <KeyboardArrowRight />
                </IconButton>
            </Box>
        </Box>
    );
};

export default ComponentSlider;